import { useAsync } from "@react-hookz/web";
import { useEffect, useState } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";

import { useEditionValue } from "shared/lib/edition";

import { useMyNews } from "scmp-app/lib/hooks";
import { useUserService } from "scmp-app/lib/user-service/hooks";
import type { hooksTrendingTopicsQuery } from "scmp-app/queries/__generated__/hooksTrendingTopicsQuery.graphql";
import { FollowAction, FollowType } from "scmp-app/scmp-sdk";

export const useTrendingTopics = () => {
  const {
    followedIds: { topicIds = [] },
  } = useMyNews();
  const environment = useRelayEnvironment();
  const edition = useEditionValue();
  const { followAction } = useUserService();

  const [newFollowTopicIds, setNewFollowTopicIds] = useState<string[]>([]);

  const [{ result }, { execute }] = useAsync(async () => {
    try {
      const trendingTopicsContent = await fetchQuery<hooksTrendingTopicsQuery>(
        environment,
        graphql`
          query hooksTrendingTopicsQuery($queueName: String) {
            queue(filter: { name: $queueName }) {
              items(first: 10) {
                edges {
                  node {
                    ... on Topic {
                      name
                      entityId
                      urlAlias
                    }
                  }
                }
              }
            }
          }
        `,
        {
          queueName: `related_topic_homepage_${edition}_`,
        },
        { fetchPolicy: "store-or-network" },
      ).toPromise();

      return trendingTopicsContent?.queue?.items?.edges;
    } catch (error) {
      console.error(error);
    }
  });

  const onFollow = async (entityId?: string) => {
    if (!entityId) return;

    if (newFollowTopicIds.includes(entityId)) {
      // Unfollow
      await followAction(FollowAction.UnFollow, FollowType.Topic, [entityId], () => {
        setNewFollowTopicIds(previous => previous.filter(id => id !== entityId));
      });
    } else {
      // Follow
      setNewFollowTopicIds(previous => [...previous, entityId]);
      await followAction(FollowAction.Follow, FollowType.Topic, [entityId]);
    }
  };

  useEffect(() => {
    void execute();
  }, [execute]);

  const topics = result
    ?.filter(
      edge =>
        edge?.node?.entityId &&
        (!topicIds.includes(edge.node.entityId) || newFollowTopicIds.includes(edge.node.entityId)),
    )
    .map(edge => ({
      entityId: edge.node.entityId,
      name: edge.node.name,
      urlAlias: edge.node.urlAlias,
    }));

  return { newFollowTopicIds, onFollow, setNewFollowTopicIds, topics: topics ?? [] };
};
